import type { Metadata, Viewport } from "next";
import { Inter } from "next/font/google";
import { headers } from "next/headers";
import "./globals.css";
import { SITE_CONFIG } from "@/lib/seo";
import {
  websiteStructuredData,
  organizationStructuredData,
  breadcrumbStructuredData,
} from "@/constant/seodata";
import { ToastProvider } from "@heroui/react";
import AmplitudeInitializer from "@/components/AmplitudeInitializer";
// import AntiZoomHandler from "@/components/AntiZoomHandler";

const inter = Inter({ subsets: ["latin"] });

export const viewport: Viewport = {
  width: "device-width",
  initialScale: 1,
  maximumScale: 1,
  userScalable: false,
};

export const metadata: Metadata = {
  title: `${SITE_CONFIG.name} | ${SITE_CONFIG.slogan}`,
  description: SITE_CONFIG.description,
  icons: {
    icon: "/images/favicon.png",
    apple: "/images/favicon.png",
  },
  verification: {
    google: "your-google-verification-code",
  },
  openGraph: {
    type: "website",
    locale: "en_US",
    url: SITE_CONFIG.url,
    title: `${SITE_CONFIG.name} | ${SITE_CONFIG.slogan}`,
    description: SITE_CONFIG.description,
    siteName: SITE_CONFIG.name,
    images: [
      {
        url: SITE_CONFIG.ogImage,
        width: 1200,
        height: 630,
        alt: SITE_CONFIG.name,
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    title: `${SITE_CONFIG.name} | ${SITE_CONFIG.slogan}`,
    description: SITE_CONFIG.description,
    images: [SITE_CONFIG.ogImage],
    creator: SITE_CONFIG.creator,
  },
  robots: {
    index: true,
    follow: true,
    googleBot: {
      index: true,
      follow: true,
      "max-video-preview": -1,
      "max-image-preview": "large",
      "max-snippet": -1,
    },
  },
  alternates: {
    canonical: SITE_CONFIG.url,
  },
  other: {
    "theme-color": "#ffffff",
    "color-scheme": "light",
    "mobile-web-app-capable": "yes",
    "apple-mobile-web-app-capable": "yes",
    "apple-mobile-web-app-status-bar-style": "default",
  },
};

export default async function BaseLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* DNS预解析和预连接优化 */}
        <link rel="preconnect" href="https://ui.prt.news" />
        <link rel="dns-prefetch" href="https://ui.prt.news" />
        <link rel="preconnect" href="https://i.prt.news" />
        <link rel="dns-prefetch" href="https://i.prt.news" />

        <meta
          name="google-site-verification"
          content="39H-PsmI1uvyQwy8JWlG_evvHz0y5FeGun9ngAvEtiA"
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover"
        />
        <meta httpEquiv="x-dns-prefetch-control" content="on" />
        <meta name="format-detection" content="telephone=no" />
        {/* 设置网站图标 */}
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/images/favicon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/images/favicon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/images/favicon.png"
        />
      </head>

      <body
        className="h-screen max-h-screen overflow-hidden"
        suppressHydrationWarning
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(websiteStructuredData),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(organizationStructuredData),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbStructuredData),
          }}
        />

        {/* <AntiZoomHandler /> */}
        <div className={`${inter.className} antialiased flex-1`}>
          {children}
        </div>
        <ToastProvider
          placement="top-center"
          toastProps={{
            timeout: 2000,
            classNames: {
              base: "z-100 !bg-black/60 !border-none !text-white",
              content: "!bg-transparent !text-white",
              title: "!text-white",
              description: "!text-white",
            },
          }}
        />
        <AmplitudeInitializer />
      </body>
    </html>
  );
}
